import React from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Shield, Users, Award, Clock, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const About = () => {
  const values = [
    {
      icon: Shield,
      title: 'Security First',
      description: 'We prioritize cybersecurity in every aspect of our managed IT services, ensuring your business data remains protected.'
    },
    {
      icon: Users,
      title: 'Expert Team',
      description: 'Our certified professionals bring years of experience in IT management and cybersecurity to serve your business needs.'
    },
    {
      icon: Award,
      title: 'Quality Service',
      description: 'We deliver exceptional managed IT services with a focus on reliability, efficiency, and customer satisfaction.'
    },
    {
      icon: Clock,
      title: '24/7 Support',
      description: 'Round-the-clock monitoring and support to ensure your IT infrastructure operates smoothly at all times.'
    }
  ];

  const stats = [
    { number: '500+', label: 'Clients Served' },
    { number: '15+', label: 'Years Experience' },
    { number: '99.9%', label: 'Uptime Guarantee' },
    { number: '24/7', label: 'Support Available' }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-primary-light text-primary-foreground py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              About Zentech Solutions
            </h1>
            <p className="text-xl lg:text-2xl text-primary-foreground/90 leading-relaxed">
              Your trusted partner for comprehensive managed IT services and cybersecurity solutions, 
              protecting and empowering businesses across the UK.
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
                Leading the Way in Managed IT Services
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                Since our founding, Zentech Solutions has been at the forefront of providing innovative 
                managed IT services and cybersecurity solutions. We understand that technology is the 
                backbone of modern business, and we're committed to ensuring your IT infrastructure 
                supports your growth and success.
              </p>
              <p className="text-lg text-muted-foreground mb-8">
                Our comprehensive approach to managed IT services encompasses everything from network 
                management and system administration to advanced cybersecurity measures and 24/7 monitoring. 
                We don't just fix problems – we prevent them.
              </p>
              <div className="space-y-3">
                {[
                  'Proactive IT monitoring and maintenance',
                  'Enterprise-grade cybersecurity solutions',
                  'Scalable cloud services and infrastructure',
                  'Dedicated support from certified professionals'
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-success flex-shrink-0" />
                    <span className="text-foreground">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:pl-8">
              <Card className="bg-gradient-to-br from-card to-accent border-none shadow-lg">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-foreground mb-6 text-center">Our Impact</h3>
                  <div className="grid grid-cols-2 gap-6">
                    {stats.map((stat, index) => (
                      <div key={index} className="text-center">
                        <div className="text-3xl font-bold text-primary mb-2">{stat.number}</div>
                        <div className="text-sm text-muted-foreground">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-muted">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Our Core Values
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              These principles guide everything we do in delivering exceptional managed IT services 
              and cybersecurity solutions to our clients.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="text-center border-none shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="p-8">
                  <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <value.icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">{value.title}</h3>
                  <p className="text-muted-foreground">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <Card className="bg-gradient-to-r from-primary to-primary-light text-primary-foreground border-none">
            <CardContent className="p-12 text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">Our Mission</h2>
              <p className="text-xl leading-relaxed max-w-4xl mx-auto mb-8">
                To empower businesses with reliable, secure, and innovative managed IT services that drive 
                growth, enhance productivity, and protect against cyber threats. We are committed to being 
                your trusted technology partner, providing proactive solutions that keep your business 
                running smoothly in an ever-evolving digital landscape.
              </p>
              <Button asChild variant="secondary" size="lg">
                <Link to="/contact">Partner With Us Today</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-muted">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Ready to Transform Your IT Infrastructure?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-3xl mx-auto">
            Discover how our managed IT services and cybersecurity expertise can help your 
            business thrive in today's digital world.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg">
              <Link to="/contact">Get Free Consultation</Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/services">Explore Our Services</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;